import { css } from "@linaria/core"
import { styled } from "@linaria/react"
import { Box, Dialog, List, sliderClasses, Theme, Typography, useMediaQuery } from "@mui/material"
import { Stack } from "@mui/material"
import { AlertTriangle, ChevronDown, XClose } from "@untitled-ui/icons-react"
import { AnimatePresence, motion } from "framer-motion"
import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Ref,
} from "react"

import Button from "./Button"
import { CustomSlider } from "./CustomSlider"
import { IconButton } from "./IconButton"
import Skeleton from "./Skeleton"
import Spinner from "./Spinner"
import { TokenIconAndTicker, TokenSkeleton } from "./Token"
import TokenEVM from "./TokenEVM"
import TokenLogo from "./TokenLogo"

import type { RichTokenInfo } from "../ao/router"
import { type TokenInfo } from "../ao/token"
import { useEthereumProvider } from "../providers/EthereumProvider"
import { INPUT_DECIMALS, NONTRANSFERABLE_TOKENS } from "../settings"
import { NumberFont, TitleFont } from "../theme"
import { opacityVariants } from "../utils/animations"
import {
  decimalEnforcer,
  parseBigIntAsNumber,
  formatTicker,
  formatNumberAuto,
  parseNumberAsBigInt,
  formatAddress,
} from "../utils/format"
import { useInput } from "../utils/hooks"

export interface TokenInputProps {
  label?: string
  tokenID?: string
  tokenInfo?: TokenInfo
  tokens: RichTokenInfo[] | null
  readOnly?: boolean
  autoFocus?: boolean
  onSubmit?: () => unknown
  loading?: boolean
  value: string | null
  onChange?: (val: string, useMax: boolean) => void
  balance?: bigint
  onTokenIdChange: (tokenId?: string) => void
  disableTokenSelect?: boolean
  variant?: "default" | "alt"
  showSuggestions?: boolean
  showSlider?: boolean
  outputText?: string
}

function TokenInputEVM(
  {
    showSlider = false,
    label,
    tokenID,
    tokenInfo,
    readOnly,
    onTokenIdChange,
    tokens,
    autoFocus,
    onSubmit,
    loading,
    onChange,
    balance,
    disableTokenSelect = false,
    value,
    variant = "default",
    showSuggestions = false,
    outputText,
    ...rest
  }: TokenInputProps,
  ref: Ref<HTMLInputElement>,
) {
  // input ref
  const inputRef = useRef<HTMLInputElement>(null)

  // forward ref
  useImperativeHandle(ref, () => inputRef.current!, [])

  // search input state
  const searchInput = useInput()

  const [tokenSelectOpen, setTokenSelectOpen] = useState(false)

  const ethProvider = useEthereumProvider()

  // active address
  const activeAddr = ethProvider.walletAddress

  const amountAsBalancePercentage = useMemo(() => {
    if (!balance || !value) return 0

    const valueNormalized = parseNumberAsBigInt(value, tokenInfo?.denomination)
    const percentage = (valueNormalized * 100n) / balance

    console.log("onAmountAsBalancePercentageChange", { valueNormalized, balance, percentage })

    return parseInt(percentage.toString())
  }, [balance, value, tokenInfo])

  const richToken = useMemo(() => tokens?.find((token) => token.id === tokenID), [tokens, tokenID])

  const [tokenAcknowledged, setTokenAcknowledged] = useState(
    localStorage.getItem(`token-approved-${tokenID}`) === "true",
  )

  useEffect(() => {
    setTokenAcknowledged(localStorage.getItem(`token-approved-${tokenID}`) === "true")

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === `token-approved-${tokenID}`) {
        setTokenAcknowledged(event.newValue === "true")
      }
    }

    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [tokenID])

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"))

  return (
    <>
      <Dialog
        fullScreen={isMobile}
        fullWidth
        maxWidth="sm"
        position="top"
        open={tokenSelectOpen}
        onClose={() => setTokenSelectOpen(false)}
      >
        <Stack gap={2}>
          <IconButton
            onClick={() => setTokenSelectOpen(false)}
            size="large"
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              zIndex: 1,
            }}
          >
            <XClose />
          </IconButton>
          <Typography variant="h5" fontFamily={TitleFont}>
            Select a token
          </Typography>
          <List
            component={Stack}
            gap={0.5}
            sx={{
              maxHeight: isMobile ? undefined : "44vh",
              overflowY: "auto",
              padding: 0,
              paddingRight: 0.5,
              marginRight: -0.5,
            }}
          >
            {tokens &&
              !showSuggestions &&
              [tokens].map(
                (list, index) =>
                  list &&
                  list.length > 0 && (
                    <Fragment key={index}>
                      {list.map((token) => (
                        <TokenEVM
                          disableHover={NONTRANSFERABLE_TOKENS.includes(token.id)}
                          disabled={NONTRANSFERABLE_TOKENS.includes(token.id)}
                          onClick={() => {
                            if (NONTRANSFERABLE_TOKENS.includes(token.id)) return
                            onTokenIdChange(token.id)
                            setTokenSelectOpen(false)
                          }}
                          ticker={token.ticker}
                          active={tokenID === token.id}
                          name={token.name}
                          logo={token.logo}
                          balance={token.balance}
                          price={token.price}
                          value={token.value}
                          denomination={token.denomination}
                          poolTvl={token.poolTvl}
                          id={token.id}
                          key={token.id}
                          showSuggestions={showSuggestions}
                          approved={token.approved}
                        />
                      ))}
                    </Fragment>
                  ),
              )}
            {tokens === null && (
              <>
                <TokenSkeleton />
                <TokenSkeleton />
                <TokenSkeleton />
                <TokenSkeleton />
              </>
            )}
            {tokens && tokens.length === 0 && <NoListed>No tokens listed...</NoListed>}
          </List>
        </Stack>
      </Dialog>
      <Dialog
        fullWidth
        maxWidth="xs"
        position="top"
        open={!!richToken && !richToken.approved && !tokenAcknowledged}
      >
        <Stack gap={2}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h5" fontFamily={TitleFont}>
              Warning
            </Typography>
            <AlertTriangle width={18} height={18} />
          </Stack>
          <Typography>
            This token is not on the Community Approved list. Always do your own research.
          </Typography>
          {richToken && (
            <TokenIconAndTicker>
              <TokenLogo tx={richToken.logo} alt={`${richToken.logo} logo`} />
              <Stack>
                <Stack direction="row" gap={1} alignItems="center">
                  <Typography variant="subtitle2" fontWeight={700}>
                    {richToken.name}
                  </Typography>
                </Stack>
                <Typography color="text.secondary" variant="caption" fontWeight={500}>
                  {richToken.ticker} • {formatAddress(richToken.id, 4)}
                </Typography>
              </Stack>
            </TokenIconAndTicker>
          )}
          <Stack
            direction="row"
            gap={1}
            sx={{
              "& button": {
                flex: 1,
              },
            }}
          >
            <Button
              className="accent"
              onClick={() => {
                localStorage.setItem(`token-approved-${tokenID}`, "true")
                setTokenAcknowledged(true)
              }}
            >
              Acknowledge
            </Button>
            <Button
              className="outlined"
              onClick={() => {
                onTokenIdChange(undefined)
              }}
            >
              Back
            </Button>
          </Stack>
        </Stack>
      </Dialog>
      <Stack direction="row" justifyContent="space-between">
        <Container>
          {label && <Typography color="secondary">{label}</Typography>}
          {!disableTokenSelect && (
            <TokenSelect
              className="outlined"
              onClick={() => {
                setTokenSelectOpen(true)
              }}
            >
              {tokenInfo ? (
                <>
                  <TokenLogo tx={tokenInfo?.logo} alt={`${tokenInfo?.name} logo`} size="small" />
                  {formatTicker((tokenInfo as TokenInfo).ticker)}
                </>
              ) : tokenID ? (
                <>
                  <Skeleton width="1.125rem" height="1.125rem" borderRadius="100%" />
                  <Skeleton width="2.6rem" height="1.25rem" />
                </>
              ) : (
                <>Select a token</>
              )}
              <ChevronDown viewBox="0 0 24 24" />
            </TokenSelect>
          )}
          {disableTokenSelect && (
            <Box sx={{ minHeight: 25 }}>
              <Typography
                variant="caption"
                sx={{
                  background: "var(--mui-palette-background-paper)",
                  borderRadius: 5,
                  paddingY: 0.25,
                  paddingX: 0.75,
                }}
                color="secondary"
              >
                {tokenInfo?.ticker}
              </Typography>
            </Box>
          )}
          {outputText && (
            <Typography
              fontFamily={NumberFont}
              variant="h3"
              sx={{
                textOverflow: "ellipsis",
                overflow: "hidden",
                height: 60,
              }}
            >
              {outputText}
            </Typography>
          )}
          {variant === "default" && !outputText && (
            <InputAndLoading>
              <Input
                type="text"
                placeholder="0"
                readOnly={readOnly}
                onKeyDown={(e) => {
                  if (e.key !== "Enter" || !onSubmit) return
                  onSubmit()
                }}
                onChange={(event) => {
                  // TODO prevent decimals beyond denomination
                  const nextInput = decimalEnforcer(event.target.value.replace(/,/g, "."))?.replace(
                    /^0+$/,
                    "0",
                  )
                  if (nextInput !== undefined) {
                    onChange?.(nextInput, false)
                  }
                }}
                pattern="^[0-9]*[.,]?[0-9]*$" // this will only prevent submitting the form
                autoFocus={autoFocus}
                ref={inputRef}
                value={value || ""}
                {...rest}
              />
              <AnimatePresence>
                {loading && (
                  <motion.div
                    className={loadingWrapperStyles}
                    variants={opacityVariants}
                    initial="hidden"
                    animate="shown"
                    exit="hidden"
                    transition={{
                      duration: 0.15,
                      ease: "easeInOut",
                    }}
                  >
                    <Loading />
                  </motion.div>
                )}
              </AnimatePresence>
            </InputAndLoading>
          )}
          {variant === "default" && !!activeAddr && (
            <BalanceSection
              style={{
                visibility: !tokenID ? "hidden" : "visible",
              }}
            >
              <div>
                <Typography color="text.secondary">Balance</Typography>
                {typeof balance === "bigint" && tokenInfo ? (
                  <Typography
                    fontFamily={NumberFont}
                    // fontSize="xl2"
                    color="text.secondary"
                    variant="h6"
                    sx={{
                      maxWidth: 200,
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      lineHeight: 1.5,
                    }}
                  >
                    {formatNumberAuto(parseBigIntAsNumber(balance, tokenInfo.denomination))}
                  </Typography>
                ) : (
                  <Skeleton
                    height="1.25rem"
                    width="60px"
                    className={css`
                      margin: 0.5rem 0;
                    `}
                  />
                )}
              </div>
              {typeof balance === "bigint" && !readOnly && (
                <Stack gap={0.5}>
                  {showSlider && typeof balance === "bigint" && tokenInfo && (
                    <CustomSlider
                      sx={{
                        alignSelf: "flex-end",
                        width: { xs: "100%", sm: "150%" },
                        marginX: 0.5,
                        padding: 0,
                        [`& .${sliderClasses.track}`]: {
                          background: "var(--mui-palette-accent-main)",
                        },
                        [`& .${sliderClasses.rail}`]: {
                          opacity: 1,
                          background: "var(--mui-palette-secondary-main)",
                        },
                      }}
                      color="accent"
                      value={amountAsBalancePercentage}
                      valueLabelDisplay="auto"
                      shiftStep={1}
                      step={1}
                      min={0}
                      max={100}
                      valueLabelFormat={(value) => `${value}%`}
                      onChange={(_event, nextInput) => {
                        const newValue = BigInt(nextInput as number) * balance

                        onChange?.(
                          parseBigIntAsNumber(
                            newValue / 100n,
                            tokenInfo?.denomination,
                            nextInput === 100 ? tokenInfo?.denomination : INPUT_DECIMALS,
                          ),
                          false,
                        )
                      }}
                    />
                  )}
                  <BalanceButtons>
                    <SmallButton
                      onClick={() => {
                        onChange?.(
                          parseBigIntAsNumber(
                            balance / 4n,
                            tokenInfo?.denomination,
                            INPUT_DECIMALS,
                          ),
                          false,
                        )
                      }}
                    >
                      25%
                    </SmallButton>
                    <SmallButton
                      onClick={() => {
                        onChange?.(
                          parseBigIntAsNumber(
                            balance / 2n,
                            tokenInfo?.denomination,
                            INPUT_DECIMALS,
                          ),
                          false,
                        )
                      }}
                    >
                      50%
                    </SmallButton>
                    <SmallButton
                      onClick={() => {
                        onChange?.(
                          parseBigIntAsNumber(
                            (balance * 2n) / 3n,
                            tokenInfo?.denomination,
                            INPUT_DECIMALS,
                          ),
                          false,
                        )
                      }}
                    >
                      75%
                    </SmallButton>
                    <SmallButton
                      onClick={() => {
                        onChange?.(parseBigIntAsNumber(balance, tokenInfo?.denomination), true)
                      }}
                    >
                      MAX
                    </SmallButton>
                  </BalanceButtons>
                </Stack>
              )}
            </BalanceSection>
          )}
        </Container>
        {variant === "alt" && !!activeAddr && (
          <Stack alignItems="flex-end" padding={1}>
            <Typography color="secondary" variant="body2">
              Current Balance
            </Typography>
            {typeof balance === "bigint" && tokenInfo ? (
              <Typography
                fontFamily={NumberFont}
                color="secondary"
                variant="body2"
                sx={{
                  maxWidth: 140,
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  lineHeight: 1.5,
                }}
              >
                {formatNumberAuto(parseBigIntAsNumber(balance, tokenInfo.denomination))}
              </Typography>
            ) : (
              <Skeleton
                height="1.25rem"
                width="60px"
                className={css`
                  margin: 0.5rem 0;
                `}
              />
            )}
          </Stack>
        )}
      </Stack>
    </>
  )
}

const InputAndLoading = styled.div`
  position: relative;
`

const loadingWrapperStyles = css`
  position: absolute;
  right: 0px;
  top: 50%;
  font-size: 1rem;
  transform: translateY(-50%);
`

const Loading = styled(Spinner)`
  width: 0.86rem;
  height: 0.86rem;
`

export const SmallButton = styled(Button)`
  color: rgb(var(--theme-color));
  font-family: "Space Grotesk";
  font-size: 0.9rem;
  padding: calc(var(--spacing) / 1.5);
  margin: 0;
  border-radius: 0;
`

const Container = styled.div`
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: calc(var(--spacing) * 1.25);
  justify-content: flex-start;
  padding: var(--spacing) calc(var(--spacing) * 1.25);
`

const TokenSelect = styled(Button)`
  width: fit-content;
  background-color: var(--mui-palette-background-default);
  border-radius: 500px;
  padding: calc(var(--spacing) / 2 - 1px) calc(var(--spacing) * 1.25);
  justify-content: space-between;
  height: 31px;
`

const BalanceSection = styled.div`
  display: flex;
  flex-direction: row;
  gap: var(--spacing);
  justify-content: space-between;
  align-items: flex-end;
`

const BalanceButtons = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: -1px;
`

export const Input = styled.input`
  padding: 0;
  margin: 0;
  outline: none;
  border: none;
  background-color: transparent;
  font-weight: 500;
  font-size: var(--xl-font-size);

  font-family: "Space Grotesk";
  width: 100%;
  line-height: 1.15em;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }

  &::placeholder {
    color: #bcbcbc;
  }

  &:-ms-input-placeholder {
    color: #bcbcbc;
  }

  &::-ms-input-placeholder {
    color: #bcbcbc;
  }
`

const TokenSearchWrapper = styled.div`
  position: relative;
  // background-color: rgba(255, 255, 255, 0.12);
  border: 1px solid var(--mui-palette-borderSecondary);
  border-radius: var(--mui-shape-borderRadius);

  svg {
    position: absolute;
    font-size: 1.1rem;
    top: 50%;
    left: 1rem;
    color: #d0d0d0;
    transform: translateY(-50%);
    transition: all 0.23s ease-in-out;
  }
`

const TokenSearchInput = styled.input`
  padding: 1.12rem 1.2rem;
  padding-left: calc(1.2rem + 1.1rem + 1.2rem);
  background-color: transparent;
  outline: none;
  border: none;
  font-size: 1.1rem;
  font-weight: 400;
  width: calc(100% - 2 * 1rem);

  &::placeholder {
    color: #d0d0d0;
  }

  &:-ms-input-placeholder {
    color: #d0d0d0;
  }

  &::-ms-input-placeholder {
    color: #d0d0d0;
  }
`

const NoListed = styled.p`
  font-size: 1rem;
  text-align: center;
  color: #505050;
  font-weight: 500;
`

export default forwardRef(TokenInputEVM)
